import type { Vec2 } from './types'

export const EPSILON = 1e-8

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y }
}

export function subtract(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y }
}

export function scale(vector: Vec2, scalar: number): Vec2 {
  return { x: vector.x * scalar, y: vector.y * scalar }
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y
}

export function lengthSquared(vector: Vec2): number {
  return dot(vector, vector)
}

export function length(vector: Vec2): number {
  return Math.sqrt(lengthSquared(vector))
}

export function distance(a: Vec2, b: Vec2): number {
  return length(subtract(a, b))
}

export function normalize(vector: Vec2): Vec2 {
  const magnitude = length(vector)
  if (magnitude <= EPSILON) return { x: 0, y: 1 }
  return scale(vector, 1 / magnitude)
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

export function wrap(value: number, size: number): number {
  if (size <= 0) return 0
  return ((value % size) + size) % size
}

export function moveToward(current: number, target: number, maximumDelta: number): number {
  if (Math.abs(target - current) <= maximumDelta) return target
  return current + Math.sign(target - current) * maximumDelta
}

export function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount
}

export function angleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from))
}

export function headingVector(heading: number): Vec2 {
  return { x: Math.sin(heading), y: Math.cos(heading) }
}

export function rightVector(heading: number): Vec2 {
  return { x: Math.cos(heading), y: -Math.sin(heading) }
}

export function finiteVec(vector: Vec2): boolean {
  return Number.isFinite(vector.x) && Number.isFinite(vector.y)
}
